import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp, Sparkles, Trophy, AlertOctagon, Star, IndianRupee, ShoppingBag, Repeat } from "lucide-react";
import { motion } from "framer-motion";

const grades = {
  excellent: { color: "#059669", bg: "rgba(5,150,105,0.15)", label: "🏆 Excellent" },
  good: { color: "#22c55e", bg: "rgba(34,197,94,0.15)", label: "🟢 Good" },
  average: { color: "#F59E0B", bg: "rgba(245,158,11,0.15)", label: "🟡 Average" },
  attention: { color: "#DC2626", bg: "rgba(220,38,38,0.15)", label: "🔴 Attention" },
};

const Dashboard = () => {
  const { receptionists, branches, user } = useOutletContext();
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const [dateFrom, setDateFrom] = useState(first.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));
  const [branchId, setBranchId] = useState("all");
  const [empId, setEmpId] = useState("all");
  const [data, setData] = useState(null);
  const [objectives, setObjectives] = useState([]);

  const applyPreset = (preset) => {
    const now = new Date();
    const toStr = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") {
      setDateFrom(toStr(now));
      setDateTo(toStr(now));
    } else if (preset === "yesterday") {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      setDateFrom(toStr(y));
      setDateTo(toStr(y));
    } else if (preset === "week") {
      const start = new Date(now);
      const day = (start.getDay() + 6) % 7; // Monday-first
      start.setDate(start.getDate() - day);
      setDateFrom(toStr(start));
      setDateTo(toStr(now));
    } else if (preset === "month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      setDateFrom(toStr(start));
      setDateTo(toStr(now));
    }
  };

  useEffect(() => {
    api.get("/meta/objectives").then((r) => setObjectives(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (branchId !== "all") params.append("branch_id", branchId);
    if (empId !== "all") params.append("employee_id", empId);
    api.get(`/dashboard/summary?${params.toString()}`).then((r) => setData(r.data));
  }, [dateFrom, dateTo, branchId, empId]);

  if (!data) return <div className="text-slate-400 font-body">Loading dashboard…</div>;

  const kpis = [
    { icon: <ShoppingBag size={16} />, label: "Upsell Count", value: data.kpis.total_upsell, tone: "gold" },
    { icon: <Repeat size={16} />, label: "Cross-Sell Count", value: data.kpis.total_cross_sell, tone: "gold" },
    { icon: <Star size={16} />, label: "Reviews Collected", value: data.kpis.reviews_collected, tone: "gold" },
    { icon: <AlertOctagon size={16} />, label: "Complaints Handled", value: data.kpis.complaints_handled, tone: "rose" },
    { icon: <Star size={16} />, label: "Avg Feedback", value: `${data.kpis.avg_feedback || 0}★`, tone: "emerald" },
    { icon: <IndianRupee size={16} />, label: "Total Revenue", value: `₹${(data.kpis.total_revenue || 0).toLocaleString("en-IN")}`, tone: "emerald" },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="overline">Executive Overview</div>
        <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight mt-1">
          Reception Command Centre
        </h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Live KPI scorecard across all branches, auto-computed against monthly targets. Filter by branch, receptionist and date to zoom in.
        </p>
      </motion.div>

      {/* Filters */}
      <Card className="bg-[#1E293B] border-[#334155] p-5 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="date-presets">
          {[
            { k: "today", label: "Today" },
            { k: "yesterday", label: "Yesterday" },
            { k: "week", label: "This Week" },
            { k: "month", label: "This Month" },
          ].map((p) => (
            <button
              key={p.k}
              onClick={() => applyPreset(p.k)}
              data-testid={`preset-${p.k}`}
              className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full bg-[#0F172A] border border-[#334155] text-slate-300 hover:border-[#C9A24B] hover:text-[#C9A24B] transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-slate-400 mb-2 block">From</Label>
            <Input
              data-testid="filter-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-[#0F172A] border-[#334155] text-white"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-slate-400 mb-2 block">To</Label>
            <Input
              data-testid="filter-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-[#0F172A] border-[#334155] text-white"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-slate-400 mb-2 block">Branch</Label>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger data-testid="filter-branch" className="bg-[#0F172A] border-[#334155] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-slate-400 mb-2 block">
              Receptionist{" "}
              <span className="text-[#C9A24B] normal-case tracking-normal">({receptionists.length} loaded)</span>
            </Label>
            <Select value={empId} onValueChange={setEmpId} disabled={user.role === "receptionist"}>
              <SelectTrigger data-testid="filter-receptionist" className="bg-[#0F172A] border-[#334155] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                <SelectItem value="all">All Receptionists</SelectItem>
                {receptionists.map((r) => (
                  <SelectItem key={r.employee_id} value={r.employee_id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((k, i) => (
          <motion.div
            key={k.label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.35 }}
          >
            <Card className="card-hover bg-[#1E293B] border-[#334155] p-5">
              <div className="flex items-center justify-between">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor:
                      k.tone === "gold"
                        ? "rgba(201,162,75,0.12)"
                        : k.tone === "rose"
                        ? "rgba(220,38,38,0.12)"
                        : "rgba(5,150,105,0.12)",
                    color:
                      k.tone === "gold"
                        ? "#C9A24B"
                        : k.tone === "rose"
                        ? "#DC2626"
                        : "#059669",
                  }}
                >
                  {k.icon}
                </div>
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mt-4">{k.label}</div>
              <div className="font-heading text-3xl text-white tracking-tight mt-1">{k.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Insights + Performance side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-[#1E293B] border-[#334155] p-6" data-testid="performance-list">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline">Receptionist Performance</div>
              <h3 className="font-heading text-2xl text-white mt-1">Weighted Scorecard</h3>
            </div>
            <Trophy className="text-[#C9A24B]" size={22} />
          </div>
          <div className="space-y-3">
            {(data.performance_by_receptionist || []).length === 0 && (
              <div className="text-slate-500 text-sm">No active receptionists yet.</div>
            )}
            {(data.performance_by_receptionist || []).map((p) => {
              const g = grades[p.grade?.code] || grades.average;
              return (
                <div
                  key={p.employee_id}
                  className="flex items-center justify-between p-4 rounded-lg bg-[#0F172A] border border-[#334155] hover:border-[#475569] transition-colors"
                >
                  <div>
                    <div className="text-white font-medium">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {p.employee_id} · {p.profile_type.replace(/_/g, " ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-heading text-2xl" style={{ color: g.color }}>
                      {p.overall_performance}%
                    </div>
                    <Badge
                      style={{ backgroundColor: g.bg, color: g.color, borderColor: g.color }}
                      className="border font-mono text-xs"
                    >
                      {g.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card
          className="bg-gradient-to-br from-[#0F172A] to-[#1a2235] border-[#C9A24B]/40 p-6 gold-glow"
          data-testid="insights-panel"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Auto Insights</div>
              <h3 className="font-heading text-2xl text-white mt-1">Signals</h3>
            </div>
            <Sparkles className="text-[#C9A24B]" size={22} />
          </div>
          <div className="space-y-3">
            {(data.insights || []).length === 0 ? (
              <div className="text-slate-400 text-sm">All quiet. Every KPI is on track.</div>
            ) : (
              data.insights.map((i, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-[#0B1120] border border-[#334155]">
                  <div
                    className="w-2 h-2 rounded-full mt-2 shrink-0"
                    style={{ backgroundColor: i.level === "danger" ? "#DC2626" : "#F59E0B" }}
                  />
                  <div className="text-sm text-slate-300 leading-relaxed">{i.msg}</div>
                </div>
              ))
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-[#334155]">
            <div className="overline mb-3">Complaint Funnel</div>
            <div className="grid grid-cols-3 gap-2">
              <FunnelTile label="Open" value={data.complaint_funnel.open} color="#F59E0B" />
              <FunnelTile label="In Progress" value={data.complaint_funnel.in_progress} color="#3B82F6" />
              <FunnelTile label="Resolved" value={data.complaint_funnel.resolved} color="#059669" />
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-[#334155]">
            <div className="overline mb-3">Dues Overview</div>
            <div className="text-2xl font-heading text-white">
              ₹{(data.dues.total_due_amount || 0).toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-slate-400 mt-1 font-body">
              {data.dues.open_count} open · {data.dues.overdue_30} overdue 30d
            </div>
          </div>
        </Card>
      </div>

      {/* 20 Reception Objectives */}
      <Card className="bg-[#1E293B] border-[#334155] p-6" data-testid="objectives-panel">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">Reception Charter</div>
            <h3 className="font-heading text-2xl text-white mt-1">20 Reception Objectives</h3>
          </div>
          <TrendingUp className="text-[#C9A24B]" size={22} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {objectives.map((o) => (
            <div
              key={o.num}
              className="p-4 rounded-lg bg-[#0F172A] border border-[#334155] hover:border-[#C9A24B]/60 transition-colors"
            >
              <div className="text-[#C9A24B] font-mono text-xs">#{String(o.num).padStart(2, "0")}</div>
              <div className="font-heading text-white text-base mt-1 leading-tight">{o.goal}</div>
              <div className="text-xs text-slate-500 mt-2 leading-relaxed">{o.day_to_day}</div>
              <div className="mt-3 text-[10px] uppercase tracking-widest font-mono text-emerald-400/80">
                {o.kpi}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

const FunnelTile = ({ label, value, color }) => (
  <div className="p-3 rounded-lg bg-[#0B1120] border border-[#334155] text-center">
    <div className="font-heading text-xl" style={{ color }}>{value}</div>
    <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mt-1">{label}</div>
  </div>
);

export default Dashboard;
